import { useEffect, useRef } from "react";

import { AUTH_STATE_CHANGED_EVENT } from "~/utils/events";
import { logger } from "~/utils/logging";
import { isMobileNative } from "~/utils/helpers";

export interface AuthStateChangeInit {
  authorized?: boolean;
  onChange?: (value: boolean) => void;
}

/**
 * Listens for auth state changes and handles accordingly.
 * This is only used in the mobile native context to handle
 * when the user logs in/out using the app's auth flow while
 * a Classroom WebView is open.
 *
 * @example
   document.dispatchEvent(
     new CustomEvent("classroom:authStateChanged", {
       detail: { authorized: true },
   }));
);
 */
export function useAuthStateChange(init?: AuthStateChangeInit) {
  const authorizedRef = useRef(Boolean(init?.authorized));
  const callbackRef = useRef(init?.onChange);

  useEffect(() => {
    if (!isMobileNative()) return;

    async function handleAuthStateChange(
      event: CustomEventInit<{ authorized: boolean }>,
    ) {
      // Return if we can't proceed
      if (!event.detail || !globalThis.navigator.onLine) return;

      // Return if there's no change in state
      if (event.detail.authorized === authorizedRef.current) return;

      try {
        // Invoke the callback
        callbackRef.current?.(event.detail.authorized);

        // Update state
        authorizedRef.current = event.detail.authorized;
      } catch (error) {
        logger.error("Failed to refresh the session", error);
      }
    }

    // Listen for auth state changes
    document.addEventListener(AUTH_STATE_CHANGED_EVENT, handleAuthStateChange);

    return () => {
      document.removeEventListener(
        AUTH_STATE_CHANGED_EVENT,
        handleAuthStateChange,
      );
    };
  }, []);

  useEffect(() => {
    // Persist the most recent version of the callback
    callbackRef.current = init?.onChange;
  }, [init]);

  return {
    authorized: authorizedRef.current,
  };
}
